/**
 * Design tokens for Version2 UI
 * These tokens provide a consistent design language across all applications
 */

export const colors = {
  // Primary brand colors
  primary: {
    50: '#e6f7f5',
    100: '#b3e7e2',
    200: '#80d7cf',
    300: '#4dc7bd',
    400: '#26b7ad',
    500: '#07988f', // Main brand color
    600: '#0b847d',
    700: '#066c66',
    800: '#055451',
    900: '#043d3c',
  },

  // Secondary colors
  secondary: {
    50: '#f5f3ff',
    100: '#ede9fe',
    200: '#ddd6fe',
    300: '#c4b5fd',
    400: '#a78bfa',
    500: '#8b5cf6',
    600: '#7c3aed',
    700: '#6d28d9',
    800: '#5b21b6',
    900: '#4c1d95',
  },

  // Semantic colors
  success: {
    light: '#52c41a',
    main: '#389e0d',
    dark: '#237804',
  },
  warning: {
    light: '#faad14',
    main: '#d48806',
    dark: '#ad6800',
  },
  error: {
    light: '#ff4d4f',
    main: '#cf1322',
    dark: '#a8071a',
  },
  info: {
    light: '#07988f',
    main: '#096dd9',
    dark: '#0050b3',
  },

  // Neutral colors
  neutral: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#e8e8e8',
    300: '#d9d9d9',
    400: '#bfbfbf',
    500: '#8c8c8c',
    600: '#595959',
    700: '#434343',
    800: '#262626',
    900: '#141414',
  },

  // Background colors (light theme)
  background: {
    default: '#ffffff',
    paper: '#fafafa',
    dark: '#141414',
  },

  // Text colors (light theme)
  text: {
    primary: 'rgba(0, 0, 0, 0.88)',
    secondary: 'rgba(0, 0, 0, 0.65)',
    disabled: 'rgba(0, 0, 0, 0.25)',
    inverse: '#ffffff',
  },
};

/**
 * Dark theme colors
 */
export const darkColors = {
  // Primary brand colors (same as light)
  primary: colors.primary,

  // Secondary colors (same as light)
  secondary: colors.secondary,

  // Semantic colors (adjusted for dark mode)
  success: {
    light: '#52c41a',
    main: '#73d13d',
    dark: '#95de64',
  },
  warning: {
    light: '#faad14',
    main: '#ffc53d',
    dark: '#ffd666',
  },
  error: {
    light: '#ff4d4f',
    main: '#ff7875',
    dark: '#ffa39e',
  },
  info: {
    light: '#07988f',
    main: '#40a9ff',
    dark: '#69c0ff',
  },

  // Neutral colors (same as light)
  neutral: colors.neutral,

  // Background colors (dark theme)
  background: {
    default: '#141414',
    paper: '#1f1f1f',
    dark: '#000000',
  },

  // Text colors (dark theme)
  text: {
    primary: 'rgba(255, 255, 255, 0.88)',
    secondary: 'rgba(255, 255, 255, 0.65)',
    disabled: 'rgba(255, 255, 255, 0.25)',
    inverse: '#000000',
  },
};

export const spacing = {
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  xxl: '48px',
  xxxl: '64px',
};

export const borderRadius = {
  none: '0',
  sm: '2px',
  md: '4px',
  lg: '8px',
  xl: '12px',
  xxl: '16px',
  full: '9999px',
};

export const shadows = {
  none: 'none',
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  xxl: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
};

export const typography = {
  fontFamily: {
    sans: '"Open Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    mono: '"Fira Code", "Courier New", monospace',
  },
  fontSize: {
    xs: '12px',
    sm: '14px',
    base: '16px',
    lg: '18px',
    xl: '20px',
    '2xl': '24px',
    '3xl': '30px',
    '4xl': '36px',
    '5xl': '48px',
  },
  fontWeight: {
    light: '300',
    normal: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
    extrabold: '800',
  },
  lineHeight: {
    none: '1',
    tight: '1.25',
    snug: '1.375',
    normal: '1.5',
    relaxed: '1.625',
    loose: '2',
  },
};

export const breakpoints = {
  xs: '480px',
  sm: '576px',
  md: '768px',
  lg: '992px',
  xl: '1200px',
  xxl: '1600px',
};

export const zIndex = {
  base: 0,
  dropdown: 1000,
  sticky: 1020,
  fixed: 1030,
  modalBackdrop: 1040,
  modal: 1050,
  popover: 1060,
  tooltip: 1070,
};

export const transitions = {
  duration: {
    fast: '150ms',
    normal: '300ms',
    slow: '500ms',
  },
  timing: {
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },
};

/**
 * Complete theme object
 */
export const theme = {
  colors,
  spacing,
  borderRadius,
  shadows,
  typography,
  breakpoints,
  zIndex,
  transitions,
};

export type Theme = typeof theme;
